import { Typography, message } from "antd";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import {
  EXPORT_OPERATOR_ABSINTHIUM_REPORT,
  VIEW_OPERATOR_ABSINTHIUM_REPORT,
} from "../../../apiServices/API_ENDPOINTS";
import { getData, postData } from "../../../apiServices/common";
import AppContent from "../../../common/AppContent";
import AppPageHeader from "../../../common/PageHeader";
import ListTable from "../../../common/ListTable";
import { ABSINTHIUM_REPORT_GENERATE_PERMISSION } from "../../../routes/permissions";
import { cglFormatDate, hasPermission } from "../../../utils/functions";
import ExtraContent from "../../Configuration/CommonComponents/ExtraContent";
import FilterComponent from "../../Configuration/CommonComponents/FilterComponent";

const getDefaultDateRange = () => ({
  start_date: moment().subtract(6, "days"),
  end_date: moment(),
});

const AbsenteeismReport = () => {
  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const lastFilterValuesRef = useRef(null);

  // Antd
  const { Title } = Typography;

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const exportAbsenteeismReport = async (filterValues) => {
    const { start_date, end_date } = filterValues || {};
    const bodyData = {
      ...(start_date && {
        start_date: moment(start_date).format("YYYY-MM-DD"),
      }),
      ...(end_date && { end_date: moment(end_date).format("YYYY-MM-DD") }),
    };
    if (
      Object.values(bodyData).length &&
      !Object.values(bodyData).some((val) => val === undefined)
    ) {
      setLoading(true);

      const query = `${EXPORT_OPERATOR_ABSINTHIUM_REPORT}`;

      const response = await getData(
        query,
        false,
        bodyData,
        { "Content-Type": "blob" },
        "arraybuffer",
      );

      if (response && response?.data) {
        // Download the file
        const outputFilename = `operator_absenteeism_report_${moment().format("YYYYMMDD_HHmmss")}.xlsx`;

        const url = URL.createObjectURL(
          new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", outputFilename);
        document.body.appendChild(link);
        link.click();
        link.remove();

        setLoading(false);
        message.success("Absenteeism Report Exported Successfully");
        refetch();
      } else {
        setLoading(false);
        message.error("Something went wrong");
      }
    }
  };

  const previewFunction = async (filterValues) => {
    lastFilterValuesRef.current = filterValues;
    setFetchError(null);
    setLoading(true);

    const bodyData = {
      ...(filterValues?.start_date && {
        start_date: moment(filterValues.start_date).format("YYYY-MM-DD"),
      }),
      ...(filterValues?.end_date && {
        end_date: moment(filterValues.end_date).format("YYYY-MM-DD"),
      }),
    };

    const query = `${VIEW_OPERATOR_ABSINTHIUM_REPORT}`;
    const response = await postData(query, bodyData);

    if (response?.code === 200) {
      setLoading(false);
      setTableData(response?.data || []);
    } else {
      setFetchError("Could not load this list.");
      setLoading(false);
      message.error(response?.message || "Something went wrong");
    }
  };

  const refetch = () => {
    setRefresh(refresh + 1);
  };

  // Load the last 7 days on mount so the page never opens blank.
  useEffect(() => {
    previewFunction(getDefaultDateRange());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tableColumns = [
    {
      title: "Sl",
      dataIndex: "sl",
      key: "sl",
      width: 60,
      align: "center",
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 120,
      render: (row) => cglFormatDate(row),
    },
    {
      title: "ID Card",
      dataIndex: "id_card",
      key: "id_card",
      width: 100,
    },
    {
      title: "Operator Name",
      dataIndex: "operator_name",
      key: "operator_name",
      width: 180,
    },
    {
      title: "Line",
      dataIndex: "line",
      key: "line",
      width: 100,
    },
    {
      title: "Supervisor",
      dataIndex: "superviser",
      key: "superviser",
      width: 150,
    },
    {
      title: "Informed in Advance?",
      dataIndex: "is_informed_advance",
      key: "is_informed_advance",
      width: 150,
      align: "center",
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      width: 150,
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      width: 150,
    },
    {
      title: "Supervisor Contacted?",
      dataIndex: "is_superviser_contacted",
      key: "is_superviser_contacted",
      width: 180,
      align: "center",
    },
    {
      title: "Worker Response",
      dataIndex: "worker_response",
      key: "worker_response",
      width: 150,
    },
  ];

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={exportAbsenteeismReport}
                hideAddButton={true}
                disabled={
                  !hasPermission([ABSINTHIUM_REPORT_GENERATE_PERMISSION])
                }
                setShowFilter={setShowFilter}
                showFilter={showFilter}
              />
            }
            title={<Title level={5}>Absenteeism Report</Title>}
          />
        }
        content={
          <>
            <div className="table" style={{ marginTop: 10 }}>
              <FilterComponent
                tableData={tableData}
                filterAPI={exportAbsenteeismReport}
                filterName={"operator_absenteeism_report_filter"}
                searchButtonText={"Export"}
                defaultOpen
                setShowFilter={setShowFilter}
                showFilter={showFilter}
                isPreview={true}
                previewFunction={previewFunction}
              />
              <ListTable
                loading={loading}
                error={fetchError}
                onRetry={() => previewFunction(lastFilterValuesRef.current || getDefaultDateRange())}
                emptyText="No records to show yet."
                dataSource={tableData}
                columns={tableColumns}
                pagination={{
                  showSizeChanger: true,
                  defaultPageSize: 10,
                  pageSizeOptions: ["10", "20", "50", "100"],
                }}
                bordered
                scroll={{ x: true }}
                rowKey={(record, idx) => record.sl || idx}
              />
            </div>
          </>
        }
      />
    </>
  );
};

export default AbsenteeismReport;
